/**
 * Monitoring API Hooks (engineer and above)
 *
 * Shapes follow api-edna spec 013 (`app/schemas/monitoring.py`). Every timestamp is
 * naive UTC (no `Z`) — parse with `parseApiUtc`, never `new Date()` directly.
 */

import { useApiData } from './client';

export interface MonitoringTable {
  label: string;
  database: string;
  table: string;
}

export interface CommitActivityBucket {
  hour: string;
  commit_hour: string;
  /** Inserts + updates committed in this UTC hour — pipeline activity, not current rows (edna#22). */
  row_count: number;
}

export interface CommitActivityResponse {
  table: string;
  database: string;
  buckets: CommitActivityBucket[];
}

export interface BackfillProgressPoint {
  ts: string;
  records_consumed: number | null;
  records_remaining: number | null;
}

export interface BackfillJob {
  provider: string;
  source_id: number;
  status: string | null;
  start_ts: string | null;
  end_ts: string | null;
  latest_ts: string | null;
  total_consumed: number | null;
  records_remaining: number | null;
  steady_state: boolean;
  /** At most 30 points, the most recent, oldest → newest. */
  series: BackfillProgressPoint[];

  // api-edna 013. Optional so the page still renders against the pre-013 API.
  /** Max `records_remaining` over the job's full history — the bar scale. */
  series_max_remaining?: number | null;
  /** Never true for failed or deleted jobs. */
  backfill_complete?: boolean;
  /** Null while backfilling, or when complete but the time is unknown. */
  backfill_completed_at?: string | null;
  /** `backfill_completed_at` + 15 min. */
  silver_ready_at?: string | null;
  /** `silver_ready_at` has passed; false when it is unknown. */
  silver_ready?: boolean;
  /** Only while backfilling, from the last hour's rate. Null when it can't be estimated. */
  projected_completed_at?: string | null;
  /** `projected_completed_at` + 15 min. */
  projected_silver_ready_at?: string | null;
}

export interface BackfillProgressResponse {
  id_project: number;
  jobs: BackfillJob[];
}

export function useMonitoringTables() {
  return useApiData<MonitoringTable[]>('/internal/monitoring/tables');
}

export function useCommitActivity(
  table: string,
  idProject?: number,
  hours?: number,
  enabled: boolean = true
) {
  const params = new URLSearchParams({ table });
  if (idProject != null) params.set('id_project', String(idProject));
  if (hours != null) params.set('hours', String(hours));
  // Blank endpoint = no fetch (useApiData skips it), so the query only runs when
  // explicitly enabled. `hours` is always sent: the API default changed (24 → 1)
  // in api-edna 013, and the page should not depend on it.
  return useApiData<CommitActivityResponse>(
    enabled ? `/internal/monitoring/commit-activity?${params.toString()}` : ''
  );
}

export function useBackfillProgress(idProject?: number) {
  return useApiData<BackfillProgressResponse>(
    idProject != null
      ? `/internal/monitoring/backfill-progress?id_project=${idProject}`
      : ''
  );
}
